'use client';

import { c as _c } from "react/compiler-runtime";
import type { KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
const NO_FOLDERS: ReadonlySet<string> = new Set();
const SEARCH_CURSOR_STEP_BY_KEY: Partial<Record<string, number>> = {
  ArrowDown: 1,
  ArrowUp: -1
};
type SearchCollapsedFolders = {
  query: string;
  folders: ReadonlySet<string>;
};
function toggleSetItem(items: ReadonlySet<string>, item: string): Set<string> {
  const next = new Set(items);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
}
function foldersForQuery(state: SearchCollapsedFolders, query: string): ReadonlySet<string> {
  return state.query === query ? state.folders : NO_FOLDERS;
}

/**
 * Folder collapse state for the file tree. Folders collapsed during a search belong to that
 * query: a new query shows every match, and closing the search brings back the folders collapsed
 * outside it.
 */
export function usePrV2FileTreeFolderCollapse(searchTerm) {
  const $ = _c(10);
  const [treeCollapsedFolders, setTreeCollapsedFolders] = useState(NO_FOLDERS);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      query: "",
      folders: NO_FOLDERS
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [searchCollapsedFolders, setSearchCollapsedFolders] = useState(t0);
  if (!searchTerm && searchCollapsedFolders.query) {
    setSearchCollapsedFolders({
      query: "",
      folders: NO_FOLDERS
    });
  }
  let t1;
  if ($[1] !== searchTerm) {
    t1 = folderPath => {
      if (!searchTerm) {
        setTreeCollapsedFolders(current => toggleSetItem(current, folderPath));
        return;
      }
      setSearchCollapsedFolders(current_0 => ({
        query: searchTerm,
        folders: toggleSetItem(foldersForQuery(current_0, searchTerm), folderPath)
      }));
    };
    $[1] = searchTerm;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleFolder = t1;
  let t2;
  if ($[3] !== searchCollapsedFolders || $[4] !== searchTerm || $[5] !== treeCollapsedFolders) {
    t2 = searchTerm ? foldersForQuery(searchCollapsedFolders, searchTerm) : treeCollapsedFolders;
    $[3] = searchCollapsedFolders;
    $[4] = searchTerm;
    $[5] = treeCollapsedFolders;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const collapsedFolders = t2;
  let t3;
  if ($[7] !== collapsedFolders || $[8] !== toggleFolder) {
    t3 = {
      collapsedFolders,
      toggleFolder
    };
    $[7] = collapsedFolders;
    $[8] = toggleFolder;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}

/**
 * Arrow keys in the search box move this cursor through the matching files, and Enter opens it.
 * It stays on the match the user moved to while that still matches, else the first match. A
 * search that ends or finds nothing, or a chosen file leaving the results, forgets the move so it
 * cannot return when that file matches again.
 */
export function usePrV2FileTreeSearchCursor(matchFilenames, openFile) {
  const $ = _c(8);
  const [chosenFilename, setChosenFilename] = useState(null);
  const isChosenMatch = chosenFilename !== null && matchFilenames.includes(chosenFilename);
  if (chosenFilename !== null && !isChosenMatch) {
    setChosenFilename(null);
  }
  const searchCursor = isChosenMatch ? chosenFilename : matchFilenames[0] ?? null;
  let t0;
  if ($[0] !== matchFilenames || $[1] !== openFile || $[2] !== searchCursor) {
    t0 = event => {
      if (event.nativeEvent.isComposing) {
        return;
      }
      if (event.key === "Enter") {
        if (searchCursor) {
          event.preventDefault();
          openFile(searchCursor);
        }
        return;
      }
      const step = SEARCH_CURSOR_STEP_BY_KEY[event.key];
      if (step === undefined || matchFilenames.length === 0) {
        return;
      }
      event.preventDefault();
      const currentIndex = searchCursor ? matchFilenames.indexOf(searchCursor) : -1;
      const nextIndex = Math.min(Math.max(currentIndex + step, 0), matchFilenames.length - 1);
      setChosenFilename(matchFilenames[nextIndex] ?? null);
    };
    $[0] = matchFilenames;
    $[1] = openFile;
    $[2] = searchCursor;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleSearchKeyDown = t0;
  const t1 = isChosenMatch ? chosenFilename : null;
  let t2;
  if ($[4] !== handleSearchKeyDown || $[5] !== searchCursor || $[6] !== t1) {
    t2 = {
      searchCursor,
      movedCursor: t1,
      handleSearchKeyDown
    };
    $[4] = handleSearchKeyDown;
    $[5] = searchCursor;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}